import React, { useState } from "react";
import { ComposableMap, Geographies, Geography, Marker } from "react-simple-maps";
import {
  FLAGSHIP_ECOSYSTEM_X_MARK,
  FLAGSHIP_XRP_ECOSYSTEM,
  getFlagshipEcosystemOrg,
  type FlagshipEcosystemId,
} from "../data/flagshipXrpEcosystem";
import {
  FLAGSHIP_REGIONS,
  FLAGSHIP_XRP_ORGS,
  getFlagshipOrg,
  type FlagshipOrg,
  type FlagshipOrgId,
} from "../data/flagshipXrpOrgs";
import { trackEvent } from "../utils/analytics";
import FlagshipRegionModal from "./FlagshipRegionModal";
import "./FlagshipWorldMap.css";

const XRPL_ECOSYSTEM_JOIN_TAGS = ["XRPL Ecosystem", "News Subscriber"] as const;

const GEO_URL = "/maps/ne_110m_admin_0_countries.geojson";
const US_STATES_URL = "/maps/ne_110m_admin_1_states_provinces_usa.geojson";
const MAP_WIDTH = 1000;
const MAP_HEIGHT = 400;

/** City-states omitted from Natural Earth 110m — shown as map hubs instead. */
const MAP_HUB_MARKERS: readonly { orgId: FlagshipOrgId; coordinates: [number, number] }[] = [
  { orgId: "xrp-asia", coordinates: [103.819, 1.352] },
];

type MapPass = "fill" | "stroke";
/** Stroke order: base first, covered last so inland edges stay bright. */
type StrokeScope = "base" | "covered";

function geoCode(properties: Record<string, unknown> | null | undefined): string {
  if (!properties) return "";
  const adm0 = properties.ADM0_A3;
  const iso = properties.ISO_A3;
  if (typeof adm0 === "string" && adm0 && adm0 !== "-99") return adm0;
  if (typeof iso === "string" && iso && iso !== "-99") return iso;
  return "";
}

function statePostal(properties: Record<string, unknown> | null | undefined): string {
  if (!properties) return "";
  const postal = properties.postal;
  if (typeof postal === "string" && postal) return postal.toUpperCase();
  const iso = properties.iso_3166_2;
  if (typeof iso === "string" && iso.includes("-")) return iso.split("-")[1]?.toUpperCase() ?? "";
  return "";
}

function orgsForCountry(properties: Record<string, unknown> | null | undefined): FlagshipOrg[] {
  if (!properties) return [];
  const continent = typeof properties.CONTINENT === "string" ? properties.CONTINENT : "";
  const code = geoCode(properties);
  return FLAGSHIP_XRP_ORGS.filter((org) => {
    if (org.stateCodes?.length) return false;
    if (org.continent && continent === org.continent) return true;
    return Boolean(code && org.countryCodes.includes(code));
  });
}

function orgForState(properties: Record<string, unknown> | null | undefined): FlagshipOrg | undefined {
  const postal = statePostal(properties);
  if (!postal) return undefined;
  return FLAGSHIP_XRP_ORGS.find((org) => org.stateCodes?.includes(postal));
}

function coveredClassName(hoverOrgId: FlagshipOrgId | null, coveringIds: readonly FlagshipOrgId[]): string {
  const isFocus = Boolean(hoverOrgId && coveringIds.includes(hoverOrgId));
  const isDimmed = Boolean(hoverOrgId && !isFocus);
  return `is-covered${isFocus ? " is-focus" : ""}${isDimmed ? " is-dimmed" : ""}`;
}

function passClassName(base: string, pass: MapPass, covered?: string): string {
  return `flagship-map-country ${base} is-${pass}${covered ? ` ${covered}` : ""}`.trim();
}

const FlagshipWorldMap: React.FC = () => {
  const [hoverOrgId, setHoverOrgId] = useState<FlagshipOrgId | null>(null);
  const [modalRegionId, setModalRegionId] = useState<string | null>(null);
  const [modalEcosystemId, setModalEcosystemId] = useState<FlagshipEcosystemId | null>(null);
  const [joinEmail, setJoinEmail] = useState("");
  const [joinStatus, setJoinStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [joinMessage, setJoinMessage] = useState("");

  const modalRegion = FLAGSHIP_REGIONS.find((region) => region.id === modalRegionId) ?? null;
  const modalOrg = modalRegion ? getFlagshipOrg(modalRegion.orgId) : null;
  const modalEcosystem = modalEcosystemId ? getFlagshipEcosystemOrg(modalEcosystemId) : null;

  function openRegion(regionId: string) {
    setModalEcosystemId(null);
    setModalRegionId(regionId);
  }

  function openEcosystem(id: FlagshipEcosystemId) {
    setModalRegionId(null);
    setModalEcosystemId(id);
  }

  function closeModal() {
    setModalRegionId(null);
    setModalEcosystemId(null);
  }

  async function handleJoinSubmit(event: React.FormEvent) {
    event.preventDefault();
    const trimmed = joinEmail.trim();
    if (!trimmed || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
      setJoinStatus("error");
      setJoinMessage("Please enter a valid email address.");
      return;
    }

    setJoinStatus("loading");
    setJoinMessage("");

    try {
      const response = await fetch("/api/subscribe-newsletter", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: trimmed,
          source: "Homepage XRPL Ecosystem Join",
          tags: [...XRPL_ECOSYSTEM_JOIN_TAGS],
        }),
      });
      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setJoinStatus("success");
        setJoinMessage("You're on the list — welcome to the XRPL ecosystem.");
        setJoinEmail("");
        trackEvent("subscribe", "xrpl-ecosystem-join", "email", 1);
      } else {
        setJoinStatus("error");
        setJoinMessage(
          typeof data.error === "string" ? data.error : "Something went wrong. Please try again.",
        );
        trackEvent("error", "xrpl-ecosystem-join", "subscription-error", 0);
      }
    } catch {
      setJoinStatus("error");
      setJoinMessage("Network error. Please check your connection and try again.");
    }
  }

  function regionIdForOrg(orgId: FlagshipOrgId): string | undefined {
    return FLAGSHIP_REGIONS.find((region) => region.orgId === orgId)?.id;
  }

  function renderCountries(pass: MapPass, strokeScope?: StrokeScope) {
    return (
      <Geographies geography={GEO_URL}>
        {({ geographies }) =>
          geographies.map((geo) => {
            const code = geoCode(geo.properties as Record<string, unknown>);
            // USA is drawn from the states layer so CA / NV can highlight separately.
            if (code === "USA") return null;

            const covering = orgsForCountry(geo.properties as Record<string, unknown>);
            const isCovered = covering.length > 0;
            const key = `${pass}-${strokeScope ?? "all"}-${geo.rsmKey}`;

            if (pass === "stroke") {
              if (strokeScope === "base" && isCovered) return null;
              if (strokeScope === "covered" && !isCovered) return null;
            }

            if (!isCovered) {
              return (
                <Geography
                  key={key}
                  geography={geo}
                  className={passClassName("", pass)}
                  style={{ outline: "none" }}
                />
              );
            }

            const primary = covering[0];
            const coveringIds = covering.map((org) => org.id);
            const covered = coveredClassName(hoverOrgId, coveringIds);

            if (pass === "stroke") {
              return (
                <Geography
                  key={key}
                  geography={geo}
                  className={passClassName("", pass, covered)}
                  style={{ outline: "none" }}
                />
              );
            }

            return (
              <Geography
                key={key}
                geography={geo}
                className={passClassName("", pass, covered)}
                onMouseEnter={() => setHoverOrgId(primary.id)}
                onMouseLeave={() => setHoverOrgId(null)}
                onClick={() => {
                  const orgId =
                    hoverOrgId && coveringIds.includes(hoverOrgId) ? hoverOrgId : primary.id;
                  const regionId = regionIdForOrg(orgId);
                  if (regionId) openRegion(regionId);
                }}
                style={{ outline: "none" }}
              />
            );
          })
        }
      </Geographies>
    );
  }

  function renderStates(pass: MapPass, strokeScope?: StrokeScope) {
    return (
      <Geographies geography={US_STATES_URL}>
        {({ geographies }) =>
          geographies.map((geo) => {
            const org = orgForState(geo.properties as Record<string, unknown>);
            const isCovered = Boolean(org);
            const key = `${pass}-${strokeScope ?? "all"}-state-${geo.rsmKey}`;

            if (pass === "stroke") {
              if (strokeScope === "base" && isCovered) return null;
              if (strokeScope === "covered" && !isCovered) return null;
            }

            if (!org) {
              return (
                <Geography
                  key={key}
                  geography={geo}
                  className={passClassName("flagship-map-state", pass)}
                  style={{ outline: "none" }}
                />
              );
            }

            const covered = coveredClassName(hoverOrgId, [org.id]);

            if (pass === "stroke") {
              return (
                <Geography
                  key={key}
                  geography={geo}
                  className={passClassName("flagship-map-state", pass, covered)}
                  style={{ outline: "none" }}
                />
              );
            }

            return (
              <Geography
                key={key}
                geography={geo}
                className={passClassName("flagship-map-state", pass, covered)}
                onMouseEnter={() => setHoverOrgId(org.id)}
                onMouseLeave={() => setHoverOrgId(null)}
                onClick={() => {
                  const regionId = regionIdForOrg(org.id);
                  if (regionId) openRegion(regionId);
                }}
                style={{ outline: "none" }}
              />
            );
          })
        }
      </Geographies>
    );
  }

  return (
    <section className="flagship-map" id="flagship-xrp-events" aria-labelledby="flagship-map-heading">
      <div className="flagship-map-inner">
        {/* Parked: flagship events timeline (years / countdowns / you-are-here). Restore above the org chips when ready. */}

        <div className="flagship-map-ecosystem" aria-labelledby="flagship-map-ecosystem-heading">
          <h3 id="flagship-map-ecosystem-heading" className="flagship-map-ecosystem-label">
            Core XRPL ecosystem
          </h3>
          <div className="flagship-map-ecosystem-stack">
            <ul className="flagship-map-ecosystem-panels">
              {FLAGSHIP_XRP_ECOSYSTEM.map((item) => (
                <li key={item.id}>
                  <div
                    className={`flagship-ecosystem-panel${item.iconMarkOnDark ? " is-dark" : ""}`}
                    style={{ backgroundColor: item.iconBg }}
                  >
                    <img
                      src={FLAGSHIP_ECOSYSTEM_X_MARK}
                      alt=""
                      className="flagship-ecosystem-panel-mark"
                      loading="lazy"
                    />
                    <div className="flagship-ecosystem-panel-hover">
                      <div className="flagship-ecosystem-panel-top">
                        <img
                          src={item.logoSrc}
                          alt=""
                          className="flagship-ecosystem-panel-logo"
                          loading="lazy"
                        />
                      </div>
                      <p className="flagship-ecosystem-panel-summary">{item.summary}</p>
                      <button
                        type="button"
                        className="flagship-ecosystem-panel-open"
                        aria-label={`Open ${item.name}`}
                        onClick={() => openEcosystem(item.id)}
                      >
                        <svg viewBox="0 0 16 16" aria-hidden="true">
                          <path
                            d="M4.5 11.5 11.5 4.5M6.5 4.5h5v5"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1.6"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <header className="flagship-map-header">
          <p className="flagship-map-kicker">Around the world</p>
          <h2 id="flagship-map-heading" className="flagship-map-title">
            Looking for the XRPL near you?
          </h2>
        </header>

        <div className="flagship-map-orgs">
          <ul className="flagship-map-org-list">
            {FLAGSHIP_REGIONS.map((region) => {
              const org = getFlagshipOrg(region.orgId);
              const imageSrc = org.logoSrc ?? "/xrpl_regions/woi.svg";
              const isHot = hoverOrgId === org.id;
              const isCool = Boolean(hoverOrgId && hoverOrgId !== org.id);
              return (
                <li key={region.id}>
                  <button
                    type="button"
                    className={`flagship-region-tile${isHot ? " is-hot" : ""}${isCool ? " is-cool" : ""}`}
                    aria-label={`${org.shortName}, ${region.label}. Open details`}
                    onMouseEnter={() => setHoverOrgId(org.id)}
                    onMouseLeave={() => setHoverOrgId(null)}
                    onFocus={() => setHoverOrgId(org.id)}
                    onBlur={() => setHoverOrgId(null)}
                    onClick={() => openRegion(region.id)}
                  >
                    <span className="flagship-region-tile-media" aria-hidden="true">
                      <img src={imageSrc} alt="" className="flagship-region-tile-image" loading="lazy" />
                    </span>
                    <span className="flagship-region-tile-name">{org.shortName}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>

        <div className="flagship-map-panel">
          <div className="flagship-map-stage" role="group" aria-label="World map of regional XRP organisations">
            <ComposableMap
              className="flagship-map-svg"
              projection="geoEquirectangular"
              width={MAP_WIDTH}
              height={MAP_HEIGHT}
              projectionConfig={{ scale: 152, center: [0, 10] }}
            >
              {/* Fills → base strokes → covered strokes last so inland edges stay as bright as the coast. */}
              {renderCountries("fill")}
              {renderStates("fill")}
              {renderCountries("stroke", "base")}
              {renderStates("stroke", "base")}
              {renderCountries("stroke", "covered")}
              {renderStates("stroke", "covered")}

              {MAP_HUB_MARKERS.map((hub) => {
                const org = getFlagshipOrg(hub.orgId);
                const isFocus = hoverOrgId === hub.orgId;
                const isDimmed = Boolean(hoverOrgId && hoverOrgId !== hub.orgId);
                return (
                  <Marker key={hub.orgId} coordinates={hub.coordinates}>
                    <g
                      className={`flagship-map-hub${isFocus ? " is-focus" : ""}${isDimmed ? " is-dimmed" : ""}`}
                      role="button"
                      tabIndex={0}
                      aria-label={`${org.shortName}, Singapore. Open details`}
                      onMouseEnter={() => setHoverOrgId(hub.orgId)}
                      onMouseLeave={() => setHoverOrgId(null)}
                      onFocus={() => setHoverOrgId(hub.orgId)}
                      onBlur={() => setHoverOrgId(null)}
                      onClick={() => {
                        const regionId = regionIdForOrg(hub.orgId);
                        if (regionId) openRegion(regionId);
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== "Enter" && event.key !== " ") return;
                        event.preventDefault();
                        const regionId = regionIdForOrg(hub.orgId);
                        if (regionId) openRegion(regionId);
                      }}
                    >
                      <circle className="flagship-map-hub-ring" r={9} />
                      <circle className="flagship-map-hub-dot" r={5.5} />
                    </g>
                  </Marker>
                );
              })}
            </ComposableMap>
          </div>
        </div>

        <div className="flagship-map-join">
          <p className="flagship-map-join-title">Join the XRPL ecosystem</p>
          <p className="flagship-map-join-lede">
            Discover events, updates, and more from across the XRP ecosystem.
          </p>
          <form className="flagship-map-join-form" onSubmit={handleJoinSubmit} noValidate>
            <label className="flagship-map-join-label" htmlFor="flagship-map-join-email">
              <span className="flagship-map-join-sr-only">Email address</span>
              <input
                id="flagship-map-join-email"
                type="email"
                className="flagship-map-join-input"
                value={joinEmail}
                onChange={(event) => {
                  setJoinEmail(event.target.value);
                  if (joinStatus === "error") {
                    setJoinStatus("idle");
                    setJoinMessage("");
                  }
                }}
                placeholder="Enter your email"
                autoComplete="email"
                inputMode="email"
                disabled={joinStatus === "loading"}
                aria-invalid={joinStatus === "error"}
                aria-describedby={joinMessage ? "flagship-map-join-message" : undefined}
              />
            </label>
            <button
              type="submit"
              className="flagship-map-join-submit"
              disabled={joinStatus === "loading" || !joinEmail.trim()}
            >
              {joinStatus === "loading" ? "Joining…" : "Join"}
            </button>
          </form>
          {joinMessage ? (
            <p
              id="flagship-map-join-message"
              className={`flagship-map-join-message is-${joinStatus}`}
              role="status"
            >
              {joinMessage}
            </p>
          ) : null}
        </div>
      </div>

      <FlagshipRegionModal
        org={modalOrg}
        regionLabel={modalRegion?.label ?? ""}
        flagEmoji={modalRegion?.flagEmoji ?? modalOrg?.flagEmoji ?? ""}
        flagSrc={modalRegion?.flagSrc ?? modalOrg?.flagSrc}
        ecosystem={modalEcosystem}
        isOpen={Boolean((modalRegion && modalOrg) || modalEcosystem)}
        onClose={closeModal}
      />
    </section>
  );
};

export default FlagshipWorldMap;
